import { useCallback, useState } from 'react';
import {
  getRunStatus as apiGetRunStatus,
  runOutcome as apiRunOutcome,
  sendChat as apiSendChat,
  type ChatReply,
} from '@/api';
import type { SkillDef, PipelineNode } from '@/mocks/pipeline';
import { buildComposePipeline, type ComposeRecommendation } from '@/pages/home/composeCanvas';
import { chatStorageKey, parseChatList } from '@/pages/home/studioPersist';

type SendChatFn = (body: {
  messages: { role: 'user' | 'assistant'; content: string }[];
  project: { id?: string; title: string; description: string; nodes: { id: string; skill_id: string }[]; edges: { from: string; to: string; field: string }[] };
}) => Promise<ChatReply>;

type RunOutcomeFn = (id: string) => Promise<{ run_id: string }>;
type GetRunStatusFn = (outcomeId: string, runId: string) => Promise<{ status?: string; run_id?: string }>;

export type UseComposeOpts = {
  projectId: string;
  sendChat?: SendChatFn;
  runOutcome?: RunOutcomeFn;
  getRunStatus?: GetRunStatusFn;
  skillMap?: Record<string, SkillDef>;
};

export function useCompose(opts: UseComposeOpts) {
  const sendChat = opts.sendChat ?? (apiSendChat as SendChatFn);
  const runOutcome = opts.runOutcome ?? (apiRunOutcome as RunOutcomeFn);
  const getRunStatus = opts.getRunStatus ?? (apiGetRunStatus as GetRunStatusFn);
  const skillMap = opts.skillMap ?? {};

  const [nodes, setNodes] = useState<PipelineNode[]>([]);

  const persist = (content: string, reply?: string) => {
    const key = chatStorageKey(opts.projectId);
    const existing = parseChatList(typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);
    const chat = existing[0] ?? {
      id: `c${Date.now()}`,
      title: content.slice(0, 42) || 'New chat',
      agentId: 'openrouter' as const,
      model: '',
      baseUrl: '',
      context: '',
      files: [],
      messages: [],
      updatedAt: 'now',
    };
    chat.messages = [
      ...chat.messages,
      { id: `m${Date.now()}`, role: 'user' as const, content, time: 'now' },
      ...(reply ? [{ id: `m${Date.now()}-r`, role: 'assistant' as const, content: reply, time: 'now' }] : []),
    ];
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, JSON.stringify([chat]));
    }
  };

  const send = useCallback(async (content: string) => {
    persist(content);
    const reply = await sendChat({
      messages: [{ role: 'user', content }],
      project: {
        id: opts.projectId,
        title: '',
        description: '',
        nodes: nodes.map((n) => ({ id: n.id, skill_id: n.skillId })),
        edges: [],
      },
    });
    persist(content, reply?.content);
  }, [sendChat, opts.projectId, nodes]);

  const applyCompose = useCallback((recs: ComposeRecommendation[]) => {
    if (!recs.length) return;
    const built = buildComposePipeline(recs, skillMap);
    if (built.nodes.length === 0) return;
    setNodes(built.nodes);
  }, [skillMap]);

  const run = useCallback(async () => {
    const start = await runOutcome(opts.projectId);
    if (start?.run_id) await getRunStatus(opts.projectId, start.run_id);
  }, [runOutcome, getRunStatus, opts.projectId]);

  return { send, applyCompose, run, nodes };
}
